// KnowledgeBase Quasar-style Form Controls plugin
// Renders HTML inputs (checkbox/radio/toggle) as Quasar-styled interactive components.
// Respects permissions and view mode to enable/disable editing.
// Emits 'kb-form-change' events for content synchronization.

import { registerMarkdownPostProcessor } from '../../services/PluginAPI.js'
import './styles/forms.css'

/**
 * View mode constants
 */
export const ViewMode = {
  EDIT: 'edit',
  PREVIEW: 'preview',
  PUBLIC: 'public' // Read-only shared view
}

/**
 * Plugin state - can be configured via setFormPluginConfig
 */
const pluginState = {
  viewMode: ViewMode.PREVIEW,
  canEdit: true, // Based on permission checks
  noteId: null,
  onFormChange: null // Optional callback for form changes
}

/**
 * Configure the form plugin state
 * Call this before rendering to set permissions and view mode
 */
export function setFormPluginConfig(config = {}) {
  if (config.viewMode !== undefined) pluginState.viewMode = config.viewMode
  if (config.canEdit !== undefined) pluginState.canEdit = config.canEdit
  if (config.noteId !== undefined) pluginState.noteId = config.noteId
  if (config.onFormChange !== undefined) pluginState.onFormChange = config.onFormChange
}

/**
 * Check if editing is allowed based on current state
 */
function isEditingAllowed() {
  // Never allow editing in public view
  if (pluginState.viewMode === ViewMode.PUBLIC) return false
  // Check permission-based canEdit flag
  return pluginState.canEdit === true
}

/**
 * Generate unique ID for form controls
 */
let formControlCounter = 0
function generateControlId(type) {
  return `kb-${type}-${++formControlCounter}`
}

/**
 * Reset control counter (for consistent IDs per render)
 */
export function resetFormControlCounter() {
  formControlCounter = 0
}

function isInsideCode(el) {
  return !!el.closest('pre, code, .kb-codeblock')
}

function extractLabel(input) {
  // Prefer wrapping <label>
  const label = input.closest('label')
  if (label) {
    // Use text content excluding the input itself
    const clone = label.cloneNode(true)
    const inp = clone.querySelector('input')
    if (inp) inp.parentElement && inp.parentElement.removeChild(inp)
    return (clone.textContent || '').trim()
  }
  // Fallback: following sibling text
  let text = ''
  let n = input.nextSibling
  while (n && n.nodeType === Node.TEXT_NODE) {
    text += n.textContent || ''
    n = n.nextSibling
  }
  return text.trim()
}

/**
 * Emit a form change event
 */
function emitFormChange(controlId, type, value, labelText, element) {
  const detail = {
    controlId,
    type,
    value,
    label: labelText,
    noteId: pluginState.noteId,
    element
  }

  // Emit custom event on document
  const event = new CustomEvent('kb-form-change', {
    bubbles: true,
    detail
  })
  document.dispatchEvent(event)

  // Call optional callback
  if (typeof pluginState.onFormChange === 'function') {
    try {
      pluginState.onFormChange(detail)
    } catch (e) {
      console.warn('[KnowledgeBase] Form change callback error:', e)
    }
  }
}

/**
 * Create interactive checkbox with Quasar styling
 */
function enhanceCheckbox(input) {
  if (!input || input.__kb_qfc_applied) return false
  if (isInsideCode(input)) return false
  if (input.type !== 'checkbox') return false
  // Skip task list checkboxes rendered by quasarTaskLists to avoid double rendering
  if (input.classList.contains('kb-task-checkbox')) return false
  if (input.closest && input.closest('.kb-task-item')) return false
  input.__kb_qfc_applied = true

  const labelText = extractLabel(input)
  const checked = !!input.checked
  const inputDisabled = !!input.disabled
  const canInteract = isEditingAllowed() && !inputDisabled
  const controlId = generateControlId('chk')

  // Create container
  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qchk' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('for', controlId)

  // Hidden but functional checkbox
  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'checkbox'
  hiddenInput.id = controlId
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract
  hiddenInput.setAttribute('aria-label', labelText || 'Checkbox')

  // Visual box
  const box = document.createElement('span')
  box.className = 'kb-qfc-box'
  const mark = document.createElement('span')
  mark.className = 'kb-qfc-mark'
  box.appendChild(mark)

  // Label text
  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  // Handle changes
  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      const newChecked = e.target.checked
      wrap.classList.toggle('is-checked', newChecked)
      emitFormChange(controlId, 'checkbox', newChecked, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(box)
  if (labelText) wrap.appendChild(text)

  // Replace input and adjacent text nodes
  const parent = input.parentElement
  if (!parent) return false

  let next = input.nextSibling
  while (next && next.nodeType === Node.TEXT_NODE) {
    const tmp = next.nextSibling
    parent.removeChild(next)
    next = tmp
  }
  parent.replaceChild(wrap, input)
  return true
}

/**
 * Create interactive radio button with Quasar styling
 */
function enhanceRadio(input) {
  if (!input || input.__kb_qfc_applied) return false
  if (isInsideCode(input)) return false
  if (input.type !== 'radio') return false
  input.__kb_qfc_applied = true

  const labelText = extractLabel(input)
  const checked = !!input.checked
  const inputDisabled = !!input.disabled
  const canInteract = isEditingAllowed() && !inputDisabled
  const controlId = generateControlId('rad')
  const groupName = input.name || `kb-radio-group-${Math.random().toString(36).slice(2, 8)}`

  // Create container
  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qrad' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('data-group', groupName)
  wrap.setAttribute('for', controlId)

  // Hidden but functional radio
  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'radio'
  hiddenInput.id = controlId
  hiddenInput.name = groupName
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract
  hiddenInput.setAttribute('aria-label', labelText || 'Radio option')

  // Visual dot
  const dot = document.createElement('span')
  dot.className = 'kb-qfc-dot'

  // Label text
  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  // Handle changes
  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      if (!e.target.checked) return
      // Update visual state for all radios in the group
      const allInGroup = document.querySelectorAll(`[data-group="${groupName}"]`)
      allInGroup.forEach((el) => el.classList.remove('is-checked'))
      wrap.classList.add('is-checked')
      emitFormChange(controlId, 'radio', labelText, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(dot)
  if (labelText) wrap.appendChild(text)

  const parent = input.parentElement
  if (!parent) return false

  let next = input.nextSibling
  while (next && next.nodeType === Node.TEXT_NODE) {
    const tmp = next.nextSibling
    parent.removeChild(next)
    next = tmp
  }
  parent.replaceChild(wrap, input)
  return true
}

/**
 * Create Quasar-style toggle switch
 * Converts checkboxes with data-toggle="true" or class="kb-toggle"
 */
function enhanceToggle(input) {
  if (!input || input.__kb_qfc_applied) return false
  if (isInsideCode(input)) return false
  if (input.type !== 'checkbox') return false

  // Only convert if explicitly marked as toggle
  const isToggle =
    input.dataset.toggle === 'true' ||
    input.classList.contains('kb-toggle') ||
    input.classList.contains('toggle')

  if (!isToggle) return false
  input.__kb_qfc_applied = true

  const labelText = extractLabel(input)
  const checked = !!input.checked
  const inputDisabled = !!input.disabled
  const canInteract = isEditingAllowed() && !inputDisabled
  const controlId = generateControlId('tgl')

  // Create container
  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qtgl' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('for', controlId)

  // Hidden but functional checkbox
  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'checkbox'
  hiddenInput.id = controlId
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract
  hiddenInput.setAttribute('aria-label', labelText || 'Toggle')

  // Toggle track
  const track = document.createElement('span')
  track.className = 'kb-qtgl-track'

  // Toggle thumb (knob)
  const thumb = document.createElement('span')
  thumb.className = 'kb-qtgl-thumb'
  track.appendChild(thumb)

  // Label text
  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  // Handle changes
  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      const newChecked = e.target.checked
      wrap.classList.toggle('is-checked', newChecked)
      emitFormChange(controlId, 'toggle', newChecked, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(track)
  if (labelText) wrap.appendChild(text)

  // Replace input and adjacent text nodes
  const parent = input.parentElement
  if (!parent) return false

  let next = input.nextSibling
  while (next && next.nodeType === Node.TEXT_NODE) {
    const tmp = next.nextSibling
    parent.removeChild(next)
    next = tmp
  }
  parent.replaceChild(wrap, input)
  return true
}

/**
 * Process markdown code blocks with special form syntax
 * Supports: ```kb-form with toggle/radio/checkbox definitions
 */
export function processFormCodeBlock(content, container) {
  const lines = content.split('\n').filter((l) => l.trim())
  const form = document.createElement('div')
  form.className = 'kb-form-group'

  for (const line of lines) {
    const trimmed = line.trim()

    // Toggle syntax: [toggle] Label or [toggle:checked] Label
    const toggleMatch = trimmed.match(/^\[toggle(?::(\w+))?\]\s*(.*)$/)
    if (toggleMatch) {
      const checked = toggleMatch[1] === 'checked' || toggleMatch[1] === 'on'
      const label = toggleMatch[2] || ''
      const el = createToggleElement(checked, label)
      form.appendChild(el)
      continue
    }

    // Checkbox syntax: [x] Label or [ ] Label
    const checkMatch = trimmed.match(/^\[(x| )\]\s*(.*)$/)
    if (checkMatch) {
      const checked = checkMatch[1].toLowerCase() === 'x'
      const label = checkMatch[2] || ''
      const el = createCheckboxElement(checked, label)
      form.appendChild(el)
      continue
    }

    // Radio syntax: (x) Label or ( ) Label or (name:x) Label
    const radioMatch = trimmed.match(/^\((?:(\w+):)?(x| )\)\s*(.*)$/)
    if (radioMatch) {
      const groupName = radioMatch[1] || 'default'
      const checked = radioMatch[2].toLowerCase() === 'x'
      const label = radioMatch[3] || ''
      const el = createRadioElement(checked, label, groupName)
      form.appendChild(el)
      continue
    }
  }

  if (form.children.length > 0) {
    container.appendChild(form)
    return true
  }
  return false
}

/**
 * Create a toggle element programmatically
 */
function createToggleElement(checked, labelText) {
  const canInteract = isEditingAllowed()
  const controlId = generateControlId('tgl')

  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qtgl' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('for', controlId)

  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'checkbox'
  hiddenInput.id = controlId
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract

  const track = document.createElement('span')
  track.className = 'kb-qtgl-track'
  const thumb = document.createElement('span')
  thumb.className = 'kb-qtgl-thumb'
  track.appendChild(thumb)

  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      wrap.classList.toggle('is-checked', e.target.checked)
      emitFormChange(controlId, 'toggle', e.target.checked, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(track)
  if (labelText) wrap.appendChild(text)

  return wrap
}

/**
 * Create a checkbox element programmatically
 */
function createCheckboxElement(checked, labelText) {
  const canInteract = isEditingAllowed()
  const controlId = generateControlId('chk')

  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qchk' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('for', controlId)

  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'checkbox'
  hiddenInput.id = controlId
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract

  const box = document.createElement('span')
  box.className = 'kb-qfc-box'
  const mark = document.createElement('span')
  mark.className = 'kb-qfc-mark'
  box.appendChild(mark)

  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      wrap.classList.toggle('is-checked', e.target.checked)
      emitFormChange(controlId, 'checkbox', e.target.checked, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(box)
  if (labelText) wrap.appendChild(text)

  return wrap
}

/**
 * Create a radio element programmatically
 */
function createRadioElement(checked, labelText, groupName = 'default') {
  const canInteract = isEditingAllowed()
  const controlId = generateControlId('rad')
  const fullGroupName = `kb-radio-${groupName}`

  const wrap = document.createElement('label')
  wrap.className =
    'kb-qfc kb-qrad' +
    (checked ? ' is-checked' : '') +
    (!canInteract ? ' is-disabled' : ' is-interactive')
  wrap.setAttribute('data-control-id', controlId)
  wrap.setAttribute('data-group', fullGroupName)
  wrap.setAttribute('for', controlId)

  const hiddenInput = document.createElement('input')
  hiddenInput.type = 'radio'
  hiddenInput.id = controlId
  hiddenInput.name = fullGroupName
  hiddenInput.className = 'kb-qfc-input'
  hiddenInput.checked = checked
  hiddenInput.disabled = !canInteract

  const dot = document.createElement('span')
  dot.className = 'kb-qfc-dot'

  const text = document.createElement('span')
  text.className = 'kb-qfc-label'
  text.textContent = labelText

  if (canInteract) {
    hiddenInput.addEventListener('change', (e) => {
      if (!e.target.checked) return
      const allInGroup = document.querySelectorAll(`[data-group="${fullGroupName}"]`)
      allInGroup.forEach((el) => el.classList.remove('is-checked'))
      wrap.classList.add('is-checked')
      emitFormChange(controlId, 'radio', labelText, labelText, wrap)
    })
  }

  wrap.appendChild(hiddenInput)
  wrap.appendChild(dot)
  if (labelText) wrap.appendChild(text)

  return wrap
}

export function registerQuasarFormsPlugin() {
  // Register post-processor for HTML inputs
  registerMarkdownPostProcessor(
    (root, context = {}) => {
      // Reset counter for consistent IDs
      resetFormControlCounter()

      // Update plugin state from context if provided
      if (context.viewMode !== undefined) pluginState.viewMode = context.viewMode
      if (context.canEdit !== undefined) pluginState.canEdit = context.canEdit
      if (context.noteId !== undefined) pluginState.noteId = context.noteId

      // First handle toggles (before general checkboxes)
      const toggleInputs = Array.from(
        root.querySelectorAll(
          'input[type="checkbox"].kb-toggle, input[type="checkbox"].toggle, input[type="checkbox"][data-toggle="true"]'
        )
      )
      for (const inp of toggleInputs) {
        try {
          enhanceToggle(inp)
        } catch {
          /* noop */
        }
      }

      // Then handle remaining checkboxes and radios
      const inputs = Array.from(
        root.querySelectorAll('input[type="checkbox"], input[type="radio"]')
      )
      for (const inp of inputs) {
        try {
          if (inp.type === 'checkbox') enhanceCheckbox(inp)
          else if (inp.type === 'radio') enhanceRadio(inp)
        } catch {
          /* noop */
        }
      }
    },
    { sortOrder: 46 }
  )
}

export default {
  registerQuasarFormsPlugin,
  setFormPluginConfig,
  ViewMode,
  resetFormControlCounter,
  // Export element creators for programmatic use
  createToggleElement,
  createCheckboxElement,
  createRadioElement,
  processFormCodeBlock
}
